import { associatesSummarySchema, type AssociatesSummary } from '@growup/accounts-shared';
import { Button, Card, Notice } from '@growup/ui';
import { useEffect, useState } from 'react';

import { download, getJson, messageOf } from '../api';

const euros = new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' });

/**
 * Associates this year (fee life cycle): how many there are, how many already
 * paid next year, how many lapse on 31/12 and how many went back to member.
 * The fee lapses by itself; renewal reminders go out by email from 1 November.
 */
export function AssociatesView() {
  const [summary, setSummary] = useState<AssociatesSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getJson('/api/members/associates/summary', associatesSummarySchema)
      .then(setSummary)
      .catch((e: unknown) => setError(messageOf(e)));
  }, []);

  if (error) return <Notice tone="danger">{error}</Notice>;
  if (!summary) return <p className="text-gu-ink-muted">A carregar…</p>;
  const next = summary.year + 1;

  return (
    <Card
      title={`Associados em ${String(summary.year)}`}
      actions={
        <Button
          variant="secondary"
          onClick={() =>
            void download('/api/members/associates.csv', `associados-${summary.today}.csv`).catch(
              (e: unknown) => setError(messageOf(e)),
            )
          }
        >
          Descarregar CSV
        </Button>
      }
    >
      <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <Stat label="Associados hoje" value={summary.associates} />
        <Stat label={`Já pagaram ${String(next)}`} value={summary.renewed} />
        <Stat label={`Acabam a 31/12/${String(summary.year)}`} value={summary.endingThisYear} />
        <Stat label="Voltaram a membro" value={summary.lapsed} />
        <Stat label="Quotas pagas este ano" value={summary.feesPaidThisYear} />
        <Stat label="Valor das quotas" value={euros.format(summary.feesPaidThisYearCents / 100)} />
      </dl>
      <p className="mt-4 text-caption text-gu-ink-muted">
        A quota caduca sozinha a 31/12 e a pessoa volta a membro. Os avisos por email saem a 1 de
        novembro (renovar), a 15 de dezembro (último aviso) e em janeiro (quota acabada); os de um
        jovem vão para o encarregado. Uma quota paga a partir de 1 de novembro vale também para o
        ano seguinte.
      </p>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="flex flex-col gap-1 border-l-2 border-gu-accent pl-3">
      <dt className="text-caption text-gu-ink-muted">{label}</dt>
      <dd className="font-display text-title">{value}</dd>
    </div>
  );
}
